import React, { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { NODE_CONFIGS, AgentNodeType } from './nodeTypes';

interface BaseNodeData {
    label: string;
    description?: string;
    [key: string]: any;
}

const BaseAgentNode: React.FC<NodeProps & { nodeType: AgentNodeType }> = memo(({ data, selected, nodeType, id }) => {
    const config = NODE_CONFIGS[nodeType];
    const nodeData = data as unknown as BaseNodeData;

    /* ── n8n-style AI Action node ───────────────────────────── */
    if (nodeType === 'aiAction') {
        return (
            <div
                className={`agent-node agent-node--aiAction ${selected ? 'agent-node--selected' : ''}`}
                style={{
                    '--node-color': config.color,
                    '--node-bg': config.bgColor,
                    '--node-border': selected ? config.color : config.borderColor,
                } as React.CSSProperties}
            >
                {/* ── ENTRY handle (left) ── */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="entry"
                    isConnectable={true}
                    className="agent-node__handle agent-node__handle--target"
                />
                <div className="ai-port-label ai-port-label--entry">ENTRY</div>

                {/* ── Header row ── */}
                <div className="agent-node__header">
                    <div className="agent-node__icon">{config.icon}</div>
                    <div className="agent-node__badge">{config.label.toUpperCase()}</div>
                    <button className="agent-node__menu" title="Node options">⋮</button>
                </div>

                {/* ── Body ── */}
                <div className="agent-node__body">
                    <div className="agent-node__title">{nodeData.label || config.label}</div>
                    <div className="agent-node__desc">{nodeData.description || config.description}</div>
                </div>

                {/* ── Model chip ── */}
                {nodeData.model && (
                    <div className="agent-node__detail">
                        <span className="agent-node__detail-chip">🧠 {nodeData.model}</span>
                    </div>
                )}

                {/* ── OUTPUT handle (right) ── */}
                <div className="ai-port-label ai-port-label--output">OUTPUT</div>
                <Handle
                    type="source"
                    position={Position.Right}
                    id="output"
                    isConnectable={true}
                    className="agent-node__handle agent-node__handle--output"
                />

                {/* ── TOOLS port section (bottom) ── */}
                <div className="ai-tools-section">
                    <span className="ai-tools-section__label">🛠️ TOOLS</span>
                </div>
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="tools"
                    isConnectable={true}
                    className="agent-node__handle agent-node__handle--tools"
                />
            </div>
        );
    }

    /* ── All other node types ─────────────────────────────── */
    return (
        <div
            className={`agent-node agent-node--${nodeType} ${selected ? 'agent-node--selected' : ''}`}
            style={{
                '--node-color': config.color,
                '--node-bg': config.bgColor,
                '--node-border': selected ? config.color : config.borderColor,
            } as React.CSSProperties}
        >
            {/* Inbound handle (not for triggers) */}
            {nodeType !== 'trigger' && (
                <Handle
                    type="target"
                    position={nodeType === 'tool' ? Position.Top : Position.Left}
                    id="entry"
                    isConnectable={true}
                    className={`agent-node__handle ${
                        nodeType === 'tool'
                            ? 'agent-node__handle--tool-top'
                            : 'agent-node__handle--target'
                    }`}
                />
            )}

            {/* Node header */}
            <div className="agent-node__header">
                <div className="agent-node__icon">{config.icon}</div>
                <div className="agent-node__badge">{config.label.toUpperCase()}</div>
                <button className="agent-node__menu" title="Node options">⋮</button>
            </div>

            {/* Node body */}
            <div className="agent-node__body">
                <div className="agent-node__title">{nodeData.label || config.label}</div>
                <div className="agent-node__desc">{nodeData.description || config.description}</div>
            </div>

            {/* Type-specific details */}
            {nodeType === 'trigger' && nodeData.triggerType && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">
                        {nodeData.triggerType === 'whatsapp' && '📱 WhatsApp'}
                        {nodeData.triggerType === 'teams' && '💬 Teams'}
                        {nodeData.triggerType === 'cron' && '⏰ Scheduled'}
                        {nodeData.triggerType === 'manual' && '👆 Manual'}
                    </span>
                </div>
            )}

            {nodeType === 'query' && nodeData.queryName && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">📋 {nodeData.queryName}</span>
                </div>
            )}

            {nodeType === 'condition' && nodeData.field && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">
                        {nodeData.field} {nodeData.operator} {nodeData.value}
                    </span>
                </div>
            )}

            {nodeType === 'apiCall' && nodeData.method && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">
                        {nodeData.method} {nodeData.url ? new URL(nodeData.url).pathname : '...'}
                    </span>
                </div>
            )}

            {nodeType === 'notify' && nodeData.channel && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">
                        {nodeData.channel === 'same' && '↩️ Reply on same channel'}
                        {nodeData.channel === 'whatsapp' && '📱 WhatsApp'}
                        {nodeData.channel === 'teams' && '💬 Teams'}
                        {nodeData.channel === 'email' && '📧 Email'}
                    </span>
                </div>
            )}

            {nodeType === 'tool' && (
                <div className="agent-node__detail">
                    <span className="agent-node__detail-chip">🔧 {(nodeData.enabledTools || []).length} tools</span>
                </div>
            )}

            {/* Outbound handle — Tool nodes use bottom, everything else uses right */}
            <Handle
                type="source"
                position={nodeType === 'tool' ? Position.Bottom : Position.Right}
                isConnectable={true}
                className={`agent-node__handle ${
                    nodeType === 'tool'
                        ? 'agent-node__handle--tool-bottom'
                        : 'agent-node__handle--source'
                }`}
            />

            {/* Condition node has a second output for "false" branch */}
            {nodeType === 'condition' && (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="false"
                    isConnectable={true}
                    className="agent-node__handle agent-node__handle--source agent-node__handle--false"
                />
            )}
        </div>
    );
});

// Export individual node components for React Flow's nodeTypes
export const TriggerNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="trigger" />;
export const QueryNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="query" />;
export const AiActionNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="aiAction" />;
export const ConditionNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="condition" />;
export const ApiCallNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="apiCall" />;
export const NotifyNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="notify" />;
export const ToolNode: React.FC<NodeProps> = (props) => <BaseAgentNode {...props} nodeType="tool" />;

// Map for React Flow
export const agentNodeTypes = {
    trigger: TriggerNode,
    query: QueryNode,
    aiAction: AiActionNode,
    condition: ConditionNode,
    apiCall: ApiCallNode,
    notify: NotifyNode,
    tool: ToolNode,
};
